import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays } from "lucide-react";
import { api, fmtDateTime } from "../lib/api";

const LEVEL_META = {
  beginner: { label: "Beginner", color: "#4ADE80" },
  intermediate: { label: "Intermediate", color: "#60A5FA" },
  advance: { label: "Advance", color: "#F59E0B" },
  focus: { label: "Focus Group", color: "#EC4899" },
  all: { label: "All levels", color: "#9D4EDD" },
};
const normLvl = (s) => {
  s = (s || "").toLowerCase();
  if (s.includes("begin")) return "beginner";
  if (s.includes("inter")) return "intermediate";
  if (s.includes("adv")) return "advance";
  if (s.includes("focus")) return "focus";
  return "all";
};

// Lets an admin pick which calendar events OR class dates a package applies to.
// source="events" (default) lists events; source="classes" lists class occurrences.
export default function EventDatePicker({ value = [], onChange, testidPrefix = "event-picker", source = "events" }) {
  const isClasses = source === "classes";
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (isClasses) {
      api.get("/admin/classes").then((r) => setItems(r.data || [])).catch(() => {});
    } else {
      api.get("/admin/events").then((r) => setItems(r.data || [])).catch(() => {});
      api.get("/calendar-categories").then((r) => setCats(r.data || [])).catch(() => {});
    }
  }, [isClasses]);

  const catOf = (e) => (isClasses ? normLvl(e.level) : (e.category || "events"));

  const catMeta = useMemo(() => {
    if (isClasses) return LEVEL_META;
    const m = {};
    (cats || []).forEach((c) => { m[c.key] = c; });
    return m;
  }, [cats, isClasses]);

  const upcoming = useMemo(() => {
    const now = new Date().toISOString();
    return (items || [])
      .filter((e) => (e.starts_at || "") >= now || value.includes(e.id))
      .sort((a, b) => (a.starts_at || "").localeCompare(b.starts_at || ""));
  }, [items, value]);

  const presentCats = useMemo(() => {
    const keys = Array.from(new Set(upcoming.map((e) => catOf(e))));
    return keys.map((k) => ({ key: k, label: catMeta[k]?.label || k, color: catMeta[k]?.color }));
  }, [upcoming, catMeta]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = filter === "all" ? upcoming : upcoming.filter((e) => catOf(e) === filter);

  const toggle = (id) => {
    const next = value.includes(id) ? value.filter((x) => x !== id) : [...value, id];
    onChange(next);
  };

  return (
    <div className="border-t border-white/10 pt-3" data-testid={`${testidPrefix}-block`}>
      <p className="text-[#D4AF37] text-xs font-semibold mb-2 flex items-center gap-1.5">
        <CalendarDays size={13} /> {isClasses ? "Applies to these class dates" : "Applies to these dates / events"}
      </p>
      <p className="text-[11px] text-[#A19DB0] mb-2">
        {isClasses
          ? "Add the classes in Calendar first, then tick every class date this pass can be used for. Members pick from these when buying, and each booking shows on the class roster to check in."
          : "Create the calendar event first, then tick every date this pass is valid for. The scanner for each selected date will count these passes as sign-ups."}
      </p>
      {presentCats.length > 1 && (
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid={`${testidPrefix}-filter`}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 mb-2 text-sm outline-none focus:border-[#D4AF37]">
          <option value="all" className="bg-[#140F1E]">All {isClasses ? "levels" : "categories"}</option>
          {presentCats.map((c) => <option key={c.key} value={c.key} className="bg-[#140F1E]">{c.label}</option>)}
        </select>
      )}
      <div className="max-h-52 overflow-y-auto space-y-1.5 pr-1" data-testid={`${testidPrefix}-list`}>
        {shown.length === 0 ? (
          <p className="text-[#A19DB0]/70 text-xs py-2">{isClasses ? "No upcoming classes. Add one in Calendar first." : "No upcoming events. Add one in Calendar first."}</p>
        ) : shown.map((e) => {
          const meta = catMeta[catOf(e)];
          const checked = value.includes(e.id);
          return (
            <label key={e.id} data-testid={`${testidPrefix}-item-${e.id}`}
              className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 cursor-pointer transition-colors ${checked ? "border-[#D4AF37]/60 bg-[#D4AF37]/[0.06]" : "border-white/10 bg-white/5 hover:bg-white/[0.08]"}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(e.id)}
                data-testid={`${testidPrefix}-check-${e.id}`} className="accent-[#D4AF37] shrink-0" />
              <span className="w-1.5 h-8 rounded-full shrink-0" style={{ background: meta?.color || e.color || "#9D4EDD" }} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm truncate">{e.title}</span>
                <span className="block text-[11px] text-[#A19DB0]">{fmtDateTime(e.starts_at)}{meta?.label ? ` · ${meta.label}` : ""}</span>
              </span>
            </label>
          );
        })}
      </div>
      {value.length > 0 && <p className="text-[11px] text-[#A19DB0] mt-2">{value.length} {isClasses ? "class date" : "date"}{value.length !== 1 ? "s" : ""} selected</p>}
    </div>
  );
}
